import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {
  LockLockableMutationMutation,
  LockReason,
} from '@github-ui/server-persisted-queries/types/lockLockableMutation'
import type {UnlockLockableMutationMutation} from '@github-ui/server-persisted-queries/types/unlockLockableMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'react-relay/hooks'
import type {Disposable, Environment} from 'relay-runtime'

type ConversationLockOptions = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
  onError?: (error: Error) => void
  onCompleted?: () => void
}

type LockedIssueRecord = Extract<
  NonNullable<LockLockableMutationMutation['lockLockable']>['lockedRecord'],
  {readonly locked: boolean}
>

type GraphQLResponse<TData> = {
  readonly data?: TData
  readonly errors?: ReadonlyArray<{readonly message?: string}>
}

const LOCK_ERROR = 'Failed to lock the conversation'
const UNLOCK_ERROR = 'Failed to unlock the conversation'

async function readGraphQLResponse<TData>(response: Response, fallbackMessage: string): Promise<TData> {
  if (!response.ok) {
    throw new Error(`${fallbackMessage} (${response.status})`)
  }

  const json = (await response.json()) as GraphQLResponse<TData> | null
  if (!json?.data) throw new Error(json?.errors?.[0]?.message ?? fallbackMessage)

  return json.data
}

// RELAY-CLEANUP(use-conversation-lock): mirror native lock/unlock into the primary Relay record
// until its canonical `locked` field migrates to TanStack.
function applyOptimisticLockedUpdate(environment: Environment, issueId: string, locked: boolean): Disposable {
  return environment.applyUpdate({
    storeUpdater: store => {
      store.get(issueId)?.setValue(locked, 'locked')
    },
  })
}

// Order matters: commit the confirmed value into Relay's base record before disposing the
// optimistic update, or the pre-edit value briefly flashes between the two writes.
function settleLockedUpdate(environment: Environment, issueId: string, locked: boolean, disposable: Disposable) {
  commitLocalUpdate(environment, store => {
    store.get(issueId)?.setValue(locked, 'locked')
  })
  disposable.dispose()
}

export function useLockConversation({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: ConversationLockOptions) {
  const environment = useRelayEnvironment()
  const relayBridgeRef = useRef<Disposable | null>(null)

  const {mutate} = useEntityMutation<LockedIssueRecord>({
    scope: {id: `conversation-lock:${issueId}`},
    mutationFn: async operation => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/conversation_lock`, {
          method: 'PUT',
          body: {lockReason: operation.fields.lockReason},
        })
      } catch (error) {
        throw new Error(LOCK_ERROR, {cause: error})
      }

      const data = await readGraphQLResponse<LockLockableMutationMutation>(response, LOCK_ERROR)
      const record = data.lockLockable?.lockedRecord
      if (!record || !('locked' in record) || record.id !== issueId) throw new Error(LOCK_ERROR)
      return record
    },
    getConfirmedOperation: record => ({
      type: 'setFields',
      id: record.id,
      fields: {locked: record.locked},
    }),
    onError: error => {
      relayBridgeRef.current?.dispose()
      relayBridgeRef.current = null
      onError?.(error)
    },
    onSuccess: record => {
      if (relayBridgeRef.current) {
        settleLockedUpdate(environment, issueId, record.locked, relayBridgeRef.current)
        relayBridgeRef.current = null
      }
      onCompleted?.()
    },
  })

  return useCallback(
    (lockReason: LockReason | null) => {
      relayBridgeRef.current?.dispose()
      relayBridgeRef.current = applyOptimisticLockedUpdate(environment, issueId, true)
      mutate({
        operation: {
          type: 'setFields',
          id: issueId,
          fields: {locked: true, lockReason},
        },
      })
    },
    [environment, issueId, mutate],
  )
}

export function useUnlockConversation({
  issueId,
  nameWithOwner,
  issueNumber,
  onError,
  onCompleted,
}: ConversationLockOptions) {
  const environment = useRelayEnvironment()
  const relayBridgeRef = useRef<Disposable | null>(null)

  const {mutate} = useEntityMutation<LockedIssueRecord>({
    scope: {id: `conversation-lock:${issueId}`},
    mutationFn: async () => {
      let response: Response
      try {
        response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/conversation_lock`, {
          method: 'DELETE',
        })
      } catch (error) {
        throw new Error(UNLOCK_ERROR, {cause: error})
      }

      const data = await readGraphQLResponse<UnlockLockableMutationMutation>(response, UNLOCK_ERROR)
      const record = data.unlockLockable?.unlockedRecord
      if (!record || !('locked' in record) || record.id !== issueId) throw new Error(UNLOCK_ERROR)
      return record
    },
    getConfirmedOperation: record => ({
      type: 'setFields',
      id: record.id,
      fields: {locked: record.locked},
    }),
    onError: error => {
      relayBridgeRef.current?.dispose()
      relayBridgeRef.current = null
      onError?.(error)
    },
    onSuccess: record => {
      if (relayBridgeRef.current) {
        settleLockedUpdate(environment, issueId, record.locked, relayBridgeRef.current)
        relayBridgeRef.current = null
      }
      onCompleted?.()
    },
  })

  return useCallback(() => {
    relayBridgeRef.current?.dispose()
    relayBridgeRef.current = applyOptimisticLockedUpdate(environment, issueId, false)
    mutate({
      operation: {
        type: 'setFields',
        id: issueId,
        fields: {locked: false},
      },
    })
  }, [environment, issueId, mutate])
}
